import { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import { get } from 'api';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import StyledTextField from 'components/styled/StyledTextField';

const LeavesTable = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [actionType, setActionType] = useState(null);
  const columns = useMemo(
    () => [
      {
        field: 'employee',
        headerName: 'Employee',
        minWidth: 200,
        renderCell: (params) => (
          <Typography>
            {params.row.user?.firstName} {params.row.user?.lastName}
          </Typography>
        ),
      },
      {
        field: 'leaveType',
        headerName: 'Leave Type',
        minWidth: 150,
      },
      {
        field: 'from',
        headerName: 'From',
        minWidth: 120,
        renderCell: (params) => moment(params.row.fromDate).format('DD MMM YYYY'),
      },
      {
        field: 'to',
        headerName: 'To',
        minWidth: 120,
        renderCell: (params) => moment(params.row.toDate).format('DD MMM YYYY'),
      },
      {
        field: 'quantity',
        headerName: 'Days',
        minWidth: 100,
      },
      {
        field: 'approvedAt',
        headerName: 'Approved At',
        minWidth: 150,
        renderCell: (params) => {
          const date = params.row?.approvedAt;
          return date ? moment(date).format('DD MMM YYYY') : '-';
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 150,
        renderCell: (params) => (
          <Chip
            label={params.row.status}
            color={
              params.row.status === 'APPROVED'
                ? 'success'
                : params.row.status === 'REJECTED'
                  ? 'error'
                  : 'warning'
            }
          />
        ),
      },
      {
        field: 'action',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        minWidth: 150,
        renderCell: (params) => {
          return (
            <Box>
              <IconButton
                disabled={params.row.status !== 'PENDING'}
                onClick={() => {
                  setSelectedLeaveId(params.row._id);
                  setActionType('approve');
                  setOpenConfirm(true);
                }}
              >
                <DoneOutlineIcon />
              </IconButton>
              <IconButton
                disabled={params.row.status !== 'PENDING'}
                onClick={() => {
                  setSelectedLeaveId(params.row._id);
                  setActionType('reject');
                  setOpenConfirm(true);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [],
  );

  const handleConfirmAction = async () => {
    if (!selectedLeaveId) return;

    if (actionType === 'approve') {
      await approveLeave(selectedLeaveId);
    }

    if (actionType === 'reject') {
      await rejectedLeave(selectedLeaveId);
    }

    setOpenConfirm(false);
    setSelectedLeaveId(null);
    setActionType(null);
  };

  const approveLeave = async (id) => {
    try {
      const res = await get(`admin/approveLeave/${id}`);

      if (res.status == 200) {
        enqueueSnackbar(res.message || 'Leave Approved', {
          variant: 'success',
          autoHideDuration: 2000,
        });
        fetchLeaves();
      } else {
        enqueueSnackbar(res.message || 'Error', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', {
        variant: 'error',
        autoHideDuration: 4000,
      });
    } finally {
      setOpenConfirm(false);
      setSelectedLeaveId(null);
    }
  };

  const rejectedLeave = async (id) => {
    try {
      const res = await get(`admin/rejectLeave/${id}`);

      if (res.status == 200) {
        enqueueSnackbar(res.message || 'Leave Rejected', {
          variant: 'success',
          autoHideDuration: 2000,
        });
        fetchLeaves();
      } else {
        enqueueSnackbar(res.message || 'Error', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', {
        variant: 'error',
        autoHideDuration: 4000,
      });
    } finally {
      setOpenConfirm(false);
      setSelectedLeaveId(null);
    }
  };

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const res = await post('/admin/getTeamLeaves', {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });
      setRows(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Error fetching leaves', {
        variant: 'error',
        autoHideDuration: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({
        ...prev,
        search,
        page: 0,
      }));
    }, 600);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchLeaves();
  }, [paginationModel]);

  return (
    <Paper sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
      <StyledTextField
        type="search"
        fullWidth
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search Team Member"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          maxWidth: { sm: 200, md: 240 },
          mb: 3,
        }}
      />
      <DataGrid
        loading={loading}
        rows={rows}
        columns={columns}
        getRowId={(row) => row._id}
        rowCount={total}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) =>
          setPaginationModel({
            page: model.page,
            pageSize: model.pageSize,
            search,
          })
        }
        pageSizeOptions={[5, 10, 20]}
      />
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>{actionType === 'approve' ? 'Approve Leave' : 'Reject Leave'}</DialogTitle>

        <DialogContent>Are you sure you want to {actionType} this leave request?</DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>

          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
          >
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LeavesTable;
